/**
 * TeamMemberPanel - Member management for a team (DRR-061)
 *
 * Three sections:
 * 1. Leadership row — Team lead + deputy lead with change buttons
 * 2. Stakeholder grid — Current members with remove action
 * 3. Add member — Actor search + add button
 *
 * Uses addTeamStakeholder / removeTeamStakeholder mutations.
 */

import { useState, useCallback, useMemo } from 'react';
import {
  Stack,
  Group,
  Text,
  Badge,
  Button,
  Table,
  ActionIcon,
  Tooltip,
  Divider,
  Paper,
  Alert,
} from '@mantine/core';
import { IconTrash, IconUserPlus, IconCrown, IconShieldCheck } from '@tabler/icons-react';
import type { GqlTeam } from '@ferp/workflow-forms';
import { useListActorsForPanelQuery, type ListActorsForPanelQuery } from '@/generated/graphql';

import { ActorSearchSelect } from './ActorSearchSelect';

type ActorData = NonNullable<ListActorsForPanelQuery['listActors']>[number];

interface TeamMemberPanelProps {
  team: GqlTeam;
  onAddStakeholder: (code: string, stakeholderId: string) => Promise<void>;
  onRemoveStakeholder: (code: string, stakeholderId: string) => Promise<void>;
  onUpdateLead: (code: string, field: 'teamLeadId' | 'deputyLeadId', actorId: string | null) => Promise<void>;
  loading?: boolean;
}

export function TeamMemberPanel({
  team,
  onAddStakeholder,
  onRemoveStakeholder,
  onUpdateLead,
  loading = false,
}: TeamMemberPanelProps) {
  const [selectedActor, setSelectedActor] = useState<string | null>(null);

  const { data: actorsData, loading: actorsLoading, refetch } = useListActorsForPanelQuery();
  const actors = actorsData?.listActors ?? [];

  // Build actor lookup map
  const actorMap = useMemo(() => {
    const map = new Map<string, ActorData>();
    actors.forEach((a) => map.set(a.id, a));
    return map;
  }, [actors]);

  // Resolve stakeholder IDs to actor data
  const stakeholders = useMemo(() => {
    return (team.stakeholderIds ?? [])
      .map((id) => actorMap.get(id))
      .filter((a): a is ActorData => a != null);
  }, [team.stakeholderIds, actorMap]);

  const handleAdd = useCallback(async () => {
    if (!selectedActor) return;
    await onAddStakeholder(team.code, selectedActor);
    setSelectedActor(null);
    refetch();
  }, [selectedActor, team.code, onAddStakeholder, refetch]);

  const handleRemove = useCallback(async (actorId: string) => {
    await onRemoveStakeholder(team.code, actorId);
    refetch();
  }, [team.code, onRemoveStakeholder, refetch]);

  const existingIds = useMemo(() => team.stakeholderIds ?? [], [team.stakeholderIds]);

  return (
    <Stack gap="md">
      {/* Leadership */}
      <Paper p="sm" withBorder>
        <Text size="sm" fw={600} mb="xs">
          <IconCrown size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
          Leadership
        </Text>
        <Group gap="md" grow>
          <ActorSearchSelect
            label="Team Lead"
            value={team.teamLeadId}
            onChange={(id) => onUpdateLead(team.code, 'teamLeadId', id)}
            placeholder="Select team lead..."
            disabled={loading}
          />
          <ActorSearchSelect
            label="Deputy Lead"
            value={team.deputyLeadId}
            onChange={(id) => onUpdateLead(team.code, 'deputyLeadId', id)}
            placeholder="Select deputy lead..."
            disabled={loading}
          />
        </Group>
      </Paper>

      <Divider />

      {/* Current Members */}
      <Text size="sm" fw={600}>
        <IconShieldCheck size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
        Stakeholders ({stakeholders.length})
      </Text>

      {stakeholders.length === 0 ? (
        <Text c="dimmed" size="sm">No stakeholders assigned yet.</Text>
      ) : (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th w={60}>Remove</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {stakeholders.map((actor) => (
              <Table.Tr key={actor.id}>
                <Table.Td>
                  <Group gap="xs">
                    <Text size="sm">{actor.name}</Text>
                    {actor.id === team.teamLeadId && <Badge size="xs" color="gold">Lead</Badge>}
                    {actor.id === team.deputyLeadId && <Badge size="xs" color="silver">Deputy</Badge>}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Badge size="xs" variant="light">{actor.actorType}</Badge>
                </Table.Td>
                <Table.Td>
                  <Tooltip label="Remove from team">
                    <ActionIcon
                      size="sm"
                      color="red"
                      variant="subtle"
                      onClick={() => handleRemove(actor.id)}
                      disabled={loading}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Divider />

      {/* Add Member */}
      <Text size="sm" fw={600}>
        <IconUserPlus size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
        Add Stakeholder
      </Text>
      <Group gap="xs" align="flex-end">
        <div style={{ flex: 1 }}>
          <ActorSearchSelect
            label=""
            value={selectedActor}
            onChange={setSelectedActor}
            excludeIds={existingIds}
            placeholder="Search for actor to add..."
            disabled={loading}
          />
        </div>
        <Button
          size="sm"
          onClick={handleAdd}
          disabled={!selectedActor || loading}
          leftSection={<IconUserPlus size={14} />}
        >
          Add
        </Button>
      </Group>
    </Stack>
  );
}
